import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CornerDownRight,
  ExternalLink,
  GitBranch,
  Layers,
  Mic,
  Pencil,
  Quote,
  Sparkles,
  Users,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import { useMeetingBySlug } from "@/application/use-cases/meetings/use-meetings.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  ragToTone,
  STATUS_LABEL,
  statusToTone,
  StatusDot,
  TASK_STATUS_LABEL,
  taskStatusToTone,
} from "@/presentation/components/status/status-dot";
import type {
  DecisionRow,
  FeatureRow,
  MeetingComponentDelta,
  MeetingGlossaryEntry,
  MeetingQuote,
  MeetingStakeholderMapEntry,
  OpenQuestionRow,
  PainPointRow,
  RiskRow,
  StakeholderRow,
  TaskRow,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/meetings/view/$slug")({
  component: MeetingRawPage,
});

function MeetingRawPage() {
  const { slug } = Route.useParams();
  const meetingQ = useMeetingBySlug(slug);

  if (meetingQ.isLoading) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col">
        <PageHeader title="Meeting" />
        <p className="text-muted-foreground px-6 py-8 text-xs italic lg:px-10">Loading…</p>
      </div>
    );
  }

  if (!meetingQ.data) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col">
        <PageHeader title="Meeting not found" />
        <p className="text-muted-foreground px-6 py-8 text-xs italic lg:px-10">
          No meeting with slug "{slug}".
        </p>
      </div>
    );
  }

  const m = meetingQ.data;
  const meetingDate = new Date(m.meeting_at);

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-10 p-6 lg:gap-14 lg:p-10">
      {/* A — Header */}
      <RouteHero
        eyebrow={m.meeting_type ?? "MEETING"}
        icon={Mic}
        numeral={format(meetingDate, "dd")}
        numeralTone="cyan"
        numeralSuffix={format(meetingDate, "MMM yyyy")}
        title={m.title}
        summary={m.summary ?? m.agenda ?? undefined}
        meta={[
          { label: "Date", value: format(meetingDate, "EEE d MMM") },
          {
            label: "Duration",
            value: m.duration_minutes ? `${m.duration_minutes} min` : "—",
            tone: "cyan",
          },
          { label: "Attendees", value: m.attendees.length, tone: "green" },
          { label: "Decisions", value: m.decisions.length, tone: "amber" },
        ]}
        actions={
          <div className="flex flex-col gap-2 print:hidden">
            <Link
              to="/meetings/$id"
              params={{ id: m.id }}
              className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)]"
            >
              <span className="inline-flex items-center gap-2">
                <Pencil className="size-3.5" />
                <span className="font-data text-[11px] tracking-[0.12em] uppercase">Edit</span>
              </span>
              <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            {m.recording_url ? (
              <a
                href={m.recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)]"
              >
                <span className="inline-flex items-center gap-2">
                  <Mic className="size-3.5" />
                  <span className="font-data text-[11px] tracking-[0.12em] uppercase">
                    Fathom recording
                  </span>
                </span>
                <ExternalLink className="size-3.5" />
              </a>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-wrap gap-2">
        {m.attendeeStakeholders.map((s) => (
          <AttendeeChip key={s.id} stakeholder={s} />
        ))}
      </div>

      {/* B — Executive summary */}
      <Section eyebrow="B · Executive summary" icon={Sparkles}>
        {m.notes ? (
          <p className="text-foreground/85 max-w-prose text-[15px] leading-relaxed whitespace-pre-line">
            {m.notes}
          </p>
        ) : (
          <EmptyHint>No executive summary recorded.</EmptyHint>
        )}
      </Section>

      {/* C — Component graph */}
      <Section eyebrow="C · Component graph & ecosystem deltas" icon={GitBranch}>
        {m.component_deltas.length === 0 ? (
          <EmptyHint>No component deltas recorded.</EmptyHint>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {m.component_deltas.map((d) => (
              <ComponentDeltaCard key={d.name} delta={d} />
            ))}
          </div>
        )}
      </Section>

      {/* D — Stakeholder map */}
      <Section eyebrow="D · Stakeholder ownership map" icon={Users}>
        {m.stakeholder_map.length === 0 ? (
          <EmptyHint>No stakeholder map recorded.</EmptyHint>
        ) : (
          <StakeholderMapTable entries={m.stakeholder_map} />
        )}
      </Section>

      {/* E — Decisions */}
      <Section eyebrow={`E · Decisions (${m.decisions.length})`}>
        {m.decisions.length === 0 ? (
          <EmptyHint>No decisions linked to this meeting.</EmptyHint>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {m.decisions.map((d) => (
              <DecisionCard key={d.id} decision={d} />
            ))}
          </div>
        )}
      </Section>

      {/* F — Risks */}
      <Section eyebrow={`F · Risks (${m.risks.length})`} icon={AlertTriangle}>
        {m.risks.length === 0 ? (
          <EmptyHint>No risks linked to this meeting.</EmptyHint>
        ) : (
          <div className="grid gap-2">
            {m.risks.map((r) => (
              <RiskRow key={r.id} risk={r} />
            ))}
          </div>
        )}
      </Section>

      {/* G — Open questions */}
      <Section eyebrow={`G · Open questions (${m.openQuestions.length})`}>
        {m.openQuestions.length === 0 ? (
          <EmptyHint>No open questions linked to this meeting.</EmptyHint>
        ) : (
          <ol className="flex flex-col gap-2">
            {m.openQuestions.map((q, idx) => (
              <OpenQuestionItem key={q.id} index={idx + 1} question={q} />
            ))}
          </ol>
        )}
      </Section>

      {/* H — Action items / tasks */}
      <Section eyebrow={`H · Action items (${m.tasks.length})`}>
        {m.tasks.length === 0 ? (
          <EmptyHint>No tasks linked to this meeting.</EmptyHint>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {m.tasks.map((t) => (
              <TaskCard key={t.id} task={t} />
            ))}
          </div>
        )}
      </Section>

      {/* I — Features */}
      <Section eyebrow={`I · Features discussed (${m.features.length})`} icon={Layers}>
        {m.features.length === 0 ? (
          <EmptyHint>No features linked to this meeting.</EmptyHint>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {m.features.map((f) => (
              <FeatureCard key={f.id} feature={f} />
            ))}
          </div>
        )}
      </Section>

      {/* J — Pain points */}
      <Section eyebrow={`J · Pain points raised (${m.painPoints.length})`}>
        {m.painPoints.length === 0 ? (
          <EmptyHint>No pain points linked to this meeting.</EmptyHint>
        ) : (
          <div className="grid gap-2">
            {m.painPoints.map((p) => (
              <PainPointRow key={p.id} painPoint={p} />
            ))}
          </div>
        )}
      </Section>

      {/* K — Glossary */}
      <Section eyebrow={`K · Glossary additions (${m.glossary.length})`}>
        {m.glossary.length === 0 ? (
          <EmptyHint>No glossary terms introduced.</EmptyHint>
        ) : (
          <dl className="grid gap-3 md:grid-cols-2">
            {m.glossary.map((g) => (
              <GlossaryEntry key={g.term} entry={g} />
            ))}
          </dl>
        )}
      </Section>

      {/* L — Verbatim quotes */}
      <Section eyebrow={`L · Verbatim anchors (${m.quotes.length})`} icon={Quote}>
        {m.quotes.length === 0 ? (
          <EmptyHint>No quote anchors recorded.</EmptyHint>
        ) : (
          <div className="flex flex-col gap-3">
            {m.quotes.map((q, idx) => (
              <QuoteCard key={idx} quote={q} />
            ))}
          </div>
        )}
      </Section>

      {/* M — Confidence flags */}
      <Section eyebrow="M · Confidence & interpretation flags">
        <ConfidenceFlagsGrid flags={m.confidence_flags} />
      </Section>

      {/* N — Cross-references */}
      <Section eyebrow="N · Cross-references" icon={CornerDownRight}>
        <CrossReferencesPanel refs={m.cross_references} />
      </Section>

      {/* O — Data payload */}
      <Section eyebrow="O · Data payload">
        <details className="border-border/40 group rounded-lg border bg-black/30 p-4 print:hidden">
          <summary className="font-data text-muted-foreground hover:text-foreground cursor-pointer text-[10px] tracking-[0.18em] uppercase">
            Show structured payload (JSON)
          </summary>
          <pre className="font-data mt-3 max-h-[600px] overflow-auto text-[11px] leading-relaxed text-[oklch(0.93_0.008_240)]">
            {JSON.stringify(
              {
                meeting: {
                  title: m.title,
                  slug: m.slug,
                  meeting_at: m.meeting_at,
                  duration_minutes: m.duration_minutes,
                  meeting_type: m.meeting_type,
                },
                attendees: m.attendeeStakeholders.map((s) => ({
                  name: s.name,
                  org: s.org,
                  area: s.area,
                })),
                decisions: m.decisions.map((d) => ({ title: d.title, status: d.status })),
                risks: m.risks.map((r) => ({ code: r.code, title: r.title, rag: r.rag })),
                open_questions: m.openQuestions.map((q) => q.question),
                features: m.features.map((f) => ({
                  title: f.title,
                  journey: f.journey,
                  status: f.status,
                })),
                pain_points: m.painPoints.map((p) => ({ title: p.title, severity: p.severity })),
                glossary: m.glossary,
                quotes: m.quotes,
                component_deltas: m.component_deltas,
                stakeholder_map: m.stakeholder_map,
                confidence_flags: m.confidence_flags,
                cross_references: m.cross_references,
                flow_steps: m.flow_steps,
              },
              null,
              2
            )}
          </pre>
        </details>
      </Section>
    </div>
  );
}

// =============================================================================
// Section helpers
// =============================================================================

function Section({
  eyebrow,
  icon: Icon,
  children,
}: {
  eyebrow: string;
  icon?: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center gap-2">
        {Icon ? <Icon className="text-muted-foreground size-3.5" /> : null}
        <h2 className="eyebrow">{eyebrow}</h2>
      </header>
      {children}
    </section>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return <p className="text-muted-foreground text-xs italic">{children}</p>;
}

// =============================================================================
// Attendee chips
// =============================================================================

function AttendeeChip({ stakeholder }: { stakeholder: StakeholderRow }) {
  const ORG_TONE: Record<StakeholderRow["org"], string> = {
    yango: "border-[oklch(0.72_0.17_190_/_0.35)] bg-[oklch(0.72_0.17_190_/_0.08)]",
    moveo: "border-[oklch(0.85_0.14_80_/_0.35)] bg-[oklch(0.85_0.14_80_/_0.08)]",
    yandex: "border-[oklch(0.78_0.18_25_/_0.35)] bg-[oklch(0.78_0.18_25_/_0.08)]",
    "3p": "border-border/60 bg-muted/40",
  };
  return (
    <div
      className={`flex items-center gap-2 rounded-full border px-3 py-1 ${ORG_TONE[stakeholder.org]}`}
    >
      <Calendar className="size-3 opacity-50" />
      <span className="text-[11px] font-medium">{stakeholder.name}</span>
      <span className="font-data text-muted-foreground text-[9px] tracking-wider uppercase">
        {stakeholder.org}
      </span>
    </div>
  );
}

// =============================================================================
// C — Component delta cards
// =============================================================================

function ComponentDeltaCard({ delta }: { delta: MeetingComponentDelta }) {
  const DIRECTION_TONE: Record<string, string> = {
    build_fresh: "text-[oklch(0.85_0.13_190)]",
    integrate: "text-[oklch(0.80_0.15_155)]",
    defer: "text-[oklch(0.85_0.14_80)]",
    unknown: "text-muted-foreground",
  };
  const tone = DIRECTION_TONE[delta.direction ?? "unknown"] ?? "text-muted-foreground";
  return (
    <div className="glow-card flex flex-col gap-3 p-4 print:break-inside-avoid">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold tracking-tight">{delta.name}</h3>
        <span
          className={`font-data text-[10px] tracking-[0.14em] uppercase ${tone}`}
          title="Direction"
        >
          {delta.direction ?? "—"}
        </span>
      </div>
      <div className="border-border/40 grid gap-2 border-t pt-2 text-xs">
        <div>
          <span className="text-muted-foreground font-data text-[9px] tracking-[0.18em] uppercase">
            B2C current
          </span>
          <p className="text-foreground/80 mt-1 leading-relaxed">{delta.b2c_current ?? "—"}</p>
        </div>
        <div>
          <span className="text-muted-foreground font-data text-[9px] tracking-[0.18em] uppercase">
            B2B target
          </span>
          <p className="text-foreground/80 mt-1 leading-relaxed">{delta.b2b_target ?? "—"}</p>
        </div>
        {delta.blockers ? (
          <div>
            <span className="text-muted-foreground font-data text-[9px] tracking-[0.18em] uppercase">
              Blockers
            </span>
            <p className="mt-1 leading-relaxed text-[oklch(0.78_0.18_25)]">{delta.blockers}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =============================================================================
// D — Stakeholder map table
// =============================================================================

function StakeholderMapTable({ entries }: { entries: MeetingStakeholderMapEntry[] }) {
  return (
    <div className="border-border/40 overflow-hidden rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="text-muted-foreground font-data text-left text-[10px] tracking-[0.14em] uppercase">
            <th className="px-3 py-2">Thread</th>
            <th className="px-3 py-2">Owner</th>
            <th className="px-3 py-2">SME</th>
            <th className="px-3 py-2">Authority gap</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.thread} className="border-border/30 border-t">
              <td className="px-3 py-2 align-top font-medium">{e.thread}</td>
              <td className="text-foreground/80 px-3 py-2 align-top">{e.owner ?? "—"}</td>
              <td className="text-foreground/80 px-3 py-2 align-top">{e.sme ?? "—"}</td>
              <td className="px-3 py-2 align-top text-[oklch(0.85_0.14_80)]">
                {e.authority_gap ? `⚠ ${e.authority_gap}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// E — Decision cards
// =============================================================================

function DecisionCard({ decision }: { decision: DecisionRow }) {
  return (
    <article className="glow-card flex flex-col gap-2 p-4 print:break-inside-avoid">
      <div className="flex items-start gap-2">
        <StatusDot tone={statusToTone(decision.status)} size="md" ring />
        <h3 className="text-sm leading-snug font-semibold tracking-tight">{decision.title}</h3>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-data text-muted-foreground text-[9px] tracking-[0.18em] uppercase">
          {STATUS_LABEL[decision.status]}
        </span>
        {decision.gate ? (
          <span className="font-data rounded-sm bg-[oklch(0.72_0.17_190_/_0.10)] px-2 py-0.5 text-[9px] tracking-[0.12em] text-[oklch(0.85_0.15_190)] uppercase">
            {decision.gate}
          </span>
        ) : null}
      </div>
      {decision.rationale ? (
        <p className="text-foreground/80 text-xs leading-relaxed">{decision.rationale}</p>
      ) : null}
      {decision.alternatives ? (
        <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
          <span className="font-data tracking-wider uppercase">Alternatives:</span>{" "}
          {decision.alternatives}
        </p>
      ) : null}
    </article>
  );
}

// =============================================================================
// F — Risks
// =============================================================================

function RiskRow({ risk }: { risk: RiskRow }) {
  return (
    <article className="border-border/40 flex items-start gap-3 rounded-lg border p-3 print:break-inside-avoid">
      <StatusDot tone={ragToTone(risk.rag)} size="lg" ring />
      <span className="font-data w-12 shrink-0 text-[11px] tracking-wider tabular-nums">
        {risk.code}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-xs font-semibold tracking-tight">{risk.title}</h3>
        {risk.description ? (
          <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
            {risk.description}
          </p>
        ) : null}
        {risk.mitigation ? (
          <p className="text-foreground/70 mt-1 text-[11px] leading-relaxed">
            <span className="font-data tracking-wider uppercase">Mitigation:</span>{" "}
            {risk.mitigation}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col items-end gap-1 text-right">
        <span
          className={`font-data text-[9px] tracking-[0.18em] uppercase text-[var(--rag-${risk.rag})]`}
        >
          {risk.rag}
        </span>
        <span className="font-data text-muted-foreground text-[9px] tracking-[0.18em] uppercase">
          {risk.severity}
        </span>
      </div>
    </article>
  );
}

// =============================================================================
// G — Open questions
// =============================================================================

function OpenQuestionItem({ index, question }: { index: number; question: OpenQuestionRow }) {
  return (
    <li className="border-border/40 flex items-start gap-3 rounded-lg border p-3 print:break-inside-avoid">
      <span className="font-data text-muted-foreground w-6 shrink-0 text-[11px] tabular-nums">
        Q{index}
      </span>
      <p className="flex-1 text-xs leading-relaxed">{question.question}</p>
      <StatusDot tone={statusToTone(question.status)} size="sm" />
    </li>
  );
}

// =============================================================================
// H — Tasks
// =============================================================================

function TaskCard({ task }: { task: TaskRow }) {
  return (
    <article className="border-border/40 flex flex-col gap-2 rounded-lg border p-3 print:break-inside-avoid">
      <div className="flex items-start gap-2">
        <StatusDot tone={taskStatusToTone(task.status)} size="md" />
        <h3 className="text-xs font-semibold tracking-tight">{task.title}</h3>
      </div>
      {task.description ? (
        <p className="text-muted-foreground text-[11px] leading-relaxed">{task.description}</p>
      ) : null}
      <div className="flex items-center gap-2">
        <span className="font-data text-muted-foreground text-[9px] tracking-[0.18em] uppercase">
          {TASK_STATUS_LABEL[task.status]}
        </span>
        <span className="font-data rounded-sm bg-[oklch(1_0_0_/_0.06)] px-1.5 py-0.5 text-[9px] tracking-[0.12em] uppercase">
          {task.priority}
        </span>
      </div>
    </article>
  );
}

// =============================================================================
// I — Features
// =============================================================================

function FeatureCard({ feature }: { feature: FeatureRow }) {
  return (
    <article className="glow-card flex flex-col gap-2 p-4 print:break-inside-avoid">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm leading-snug font-semibold tracking-tight">{feature.title}</h3>
        {feature.journey ? (
          <span className="font-data shrink-0 rounded-sm bg-[oklch(0.72_0.17_190_/_0.10)] px-2 py-0.5 text-[9px] tracking-[0.12em] text-[oklch(0.85_0.15_190)] uppercase">
            {feature.journey}
          </span>
        ) : null}
      </div>
      {feature.subtitle ? (
        <p className="text-foreground/75 text-xs leading-snug">{feature.subtitle}</p>
      ) : null}
      {feature.description ? (
        <p className="text-foreground/80 text-xs leading-relaxed">{feature.description}</p>
      ) : null}
      <div className="flex items-center gap-1.5">
        <StatusDot
          tone={
            (feature.status === "deferred" ? "open" : statusToTone(feature.status)) as Parameters<
              typeof StatusDot
            >[0]["tone"]
          }
          size="sm"
        />
        <span className="font-data text-muted-foreground text-[9px] tracking-[0.18em] uppercase">
          {feature.status}
        </span>
      </div>
    </article>
  );
}

// =============================================================================
// J — Pain points
// =============================================================================

function PainPointRow({ painPoint }: { painPoint: PainPointRow }) {
  const SEVERITY_TONE: Record<string, "red" | "amber" | "green"> = {
    high: "red",
    medium: "amber",
    low: "green",
  };
  return (
    <article className="border-border/40 flex items-start gap-3 rounded-lg border p-3 print:break-inside-avoid">
      <StatusDot tone={SEVERITY_TONE[painPoint.severity]} size="md" ring />
      <div className="min-w-0 flex-1">
        <h3 className="text-xs font-semibold tracking-tight">{painPoint.title}</h3>
        {painPoint.description ? (
          <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
            {painPoint.description}
          </p>
        ) : null}
      </div>
      <span className="font-data text-muted-foreground text-[9px] tracking-[0.18em] uppercase">
        {painPoint.severity}
      </span>
    </article>
  );
}

// =============================================================================
// K — Glossary
// =============================================================================

function GlossaryEntry({ entry }: { entry: MeetingGlossaryEntry }) {
  return (
    <div className="border-border/40 rounded-lg border p-3 print:break-inside-avoid">
      <dt className="flex items-baseline gap-2">
        <span className="text-sm font-semibold tracking-tight">{entry.term}</span>
        {entry.gloss ? (
          <span className="text-muted-foreground text-[11px] italic">({entry.gloss})</span>
        ) : null}
      </dt>
      <dd className="text-foreground/80 mt-1 text-[11px] leading-relaxed">{entry.meaning}</dd>
    </div>
  );
}

// =============================================================================
// L — Quotes
// =============================================================================

function QuoteCard({ quote }: { quote: MeetingQuote }) {
  const WEIGHT_TONE: Record<string, string> = {
    "load-bearing": "border-[oklch(0.78_0.18_25_/_0.35)]",
    "operational anchor": "border-[oklch(0.85_0.14_80_/_0.35)]",
    hedged: "border-border/40",
  };
  const border = WEIGHT_TONE[quote.weight ?? ""] ?? "border-border/40";
  return (
    <blockquote
      className={`relative rounded-lg border-l-2 ${border} bg-muted/20 py-3 pr-4 pl-4 print:break-inside-avoid`}
    >
      <p className="text-sm leading-relaxed italic">"{quote.text}"</p>
      <footer className="text-muted-foreground mt-2 flex items-center gap-3 text-[11px]">
        <span className="font-medium">— {quote.speaker}</span>
        {quote.fathom_url ? (
          <a
            href={quote.fathom_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-data inline-flex items-center gap-1 tracking-wider uppercase tabular-nums hover:text-[oklch(0.85_0.15_190)]"
          >
            {quote.timestamp}
            <ExternalLink className="size-2.5" />
          </a>
        ) : (
          <span className="font-data tracking-wider tabular-nums">{quote.timestamp}</span>
        )}
        {quote.weight ? (
          <span className="font-data ml-auto tracking-[0.18em] uppercase">{quote.weight}</span>
        ) : null}
      </footer>
    </blockquote>
  );
}

// =============================================================================
// M — Confidence flags
// =============================================================================

function ConfidenceFlagsGrid({
  flags,
}: {
  flags: { inferred?: string[]; carried_not_addressed?: string[]; hedged?: string[] };
}) {
  const cols = [
    { key: "inferred", label: "Inferred", items: flags.inferred ?? [], tone: "amber" as const },
    {
      key: "carried_not_addressed",
      label: "Carried · not addressed",
      items: flags.carried_not_addressed ?? [],
      tone: "open" as const,
    },
    { key: "hedged", label: "Hedged", items: flags.hedged ?? [], tone: "cyan" as const },
  ];
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {cols.map((col) => (
        <div
          key={col.key}
          className="border-border/40 flex flex-col gap-2 rounded-lg border p-3 print:break-inside-avoid"
        >
          <div className="flex items-center gap-2">
            <StatusDot tone={col.tone} size="sm" />
            <h3 className="font-data text-[10px] tracking-[0.18em] uppercase">{col.label}</h3>
            <span className="text-muted-foreground font-data ml-auto text-[10px] tabular-nums">
              {col.items.length}
            </span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {col.items.length === 0 ? (
              <li className="text-muted-foreground text-[11px] italic">None</li>
            ) : (
              col.items.map((item) => (
                <li
                  key={item}
                  className="text-foreground/80 border-border/30 border-t pt-1.5 text-[11px] leading-relaxed first:border-t-0 first:pt-0"
                >
                  {item}
                </li>
              ))
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// N — Cross-references
// =============================================================================

interface CrossRefBackward {
  type: string;
  ref: string;
  relationship: string;
  note?: string | null;
}
interface CrossRefForward {
  thread: string;
  gates: string;
}

function CrossReferencesPanel({
  refs,
}: {
  refs: { backward?: CrossRefBackward[]; forward?: CrossRefForward[] };
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="border-border/40 flex flex-col gap-2 rounded-lg border p-3">
        <h3 className="font-data text-[10px] tracking-[0.18em] uppercase">Backward</h3>
        {(refs.backward ?? []).length === 0 ? (
          <EmptyHint>No prior items referenced.</EmptyHint>
        ) : (
          <ul className="flex flex-col gap-2">
            {(refs.backward ?? []).map((r, idx) => (
              <li key={idx} className="text-[11px] leading-relaxed">
                <span className="font-data text-muted-foreground tracking-wider uppercase">
                  {r.type}
                </span>{" "}
                <span className="font-semibold">{r.ref}</span>
                <span className="text-muted-foreground"> · {r.relationship}</span>
                {r.note ? (
                  <p className="text-foreground/70 mt-0.5 leading-relaxed">{r.note}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="border-border/40 flex flex-col gap-2 rounded-lg border p-3">
        <h3 className="font-data text-[10px] tracking-[0.18em] uppercase">Forward</h3>
        {(refs.forward ?? []).length === 0 ? (
          <EmptyHint>No forward threads opened.</EmptyHint>
        ) : (
          <ul className="flex flex-col gap-2">
            {(refs.forward ?? []).map((r, idx) => (
              <li key={idx} className="text-[11px] leading-relaxed">
                <span className="font-semibold">{r.thread}</span>
                <p className="text-muted-foreground mt-0.5">{r.gates}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
